<template>
  <page :name="domain" :title="title">
    <template #breadcrumbs>
      <pf-breadcrumb-item :to="{ name: 'services' }">{{ $t('My Hosting') }}</pf-breadcrumb-item>
    </template>

    <pf-page-section>
      <pf-empty-state v-if="servicesState.updating">
        <pf-empty-state-header :title="$t('Caricamento dei servizi')" heading-level="h4">
          <template #icon>
            <pf-empty-state-icon>
              <pf-spinner />
            </pf-empty-state-icon>
          </template>
        </pf-empty-state-header>
      </pf-empty-state>

      <pf-empty-state
        v-else-if="
          services &&
          !services.pannello &&
          !services.posta &&
          !services.pecs &&
          !services.dns &&
          !services.id_registrazione_dominio &&
          services.redirect === undefined
        "
      >
        <template #icon>
          <pf-empty-state-icon>
            <magnifying-glass-icon />
          </pf-empty-state-icon>
        </template>
        <pf-empty-state-body>
          {{ $t('Nessun dettaglio disponibile') }}
        </pf-empty-state-body>
      </pf-empty-state>

      <pf-tabs v-else-if="services" box>
        <pf-tab v-if="services.pannello" :title="titlePannello" ouia-id="service-tab-pannello" class="pf-v6-u-p-md">
          <template #icon><circle-nodes-icon /></template>

          <service-detail-pannello
            :id-pannello="services.pannello.id"
            :dominio="services.dominio"
            :pannello="services.pannello"
            :currency="login.currency"
            :with-developers-grants="login.grants.developer"
            :show-dns-login="services.dns"
          />
        </pf-tab>

        <pf-tab v-if="services.posta" :title="$t('Email')" ouia-id="service-tab-email" class="pf-v6-u-p-md">
          <template #icon><envelope-icon /></template>

          <service-detail-pannello
            :id-pannello="services.posta.id"
            :dominio="services.dominio"
            :pannello="services.posta"
            :currency="login.currency"
            :with-developers-grants="login.grants.developer"
          />
        </pf-tab>

        <pf-tab v-if="services.pecs" :title="$t('PEC')" ouia-id="service-tab-pec" class="pf-v6-u-p-md">
          <template #icon><envelope-icon /></template>

          <pf-form component="div">
            <form-group :name="$t('Caselle di posta elettronica certificata')">
              <pf-list plain bordered class="pf-v6-u-mt-md" style="max-width: 80ch">
                <pf-list-item v-for="pec in services.pecs" :key="pec">
                  <pf-flex justify-content="space-between" style="width: 100%">
                    <pf-flex-item>
                      <a :href="`mailto:${pec}@${services.dominio}`" target="_blank" rel="noreferer">{{ pec }}@{{ services.dominio }}</a>
                    </pf-flex-item>
                    <pf-flex-item>
                      <pf-button variant="control" small :href="pecWebmailUrl" target="_blank" rel="noreferer">
                        <template #icon>
                          <arrow-up-right-from-square-icon />
                        </template>
                        {{ $t('Webmail') }}
                      </pf-button>
                    </pf-flex-item>
                  </pf-flex>
                </pf-list-item>
              </pf-list>
            </form-group>
          </pf-form>
        </pf-tab>

        <pf-tab v-if="services.redirect" :title="$t('Redirect')" ouia-id="service-tab-redirect" class="pf-v6-u-p-md">
          <template #icon><arrow-icon /></template>

          <pf-form component="div">
            <form-group :name="$t('Destinazione')" style="max-width: 80ch">
              <form-input
                id="service-redirect"
                :disabled="!login.grants.developer"
                name="redirect"
                v-model="services.redirect.destinazione"
                :title="$t('Salva')"
                @save="save"
              />
            </form-group>
          </pf-form>
        </pf-tab>

        <pf-tab v-if="services.dns" :title="$t('DNS')" ouia-id="service-tab-dns" class="pf-v6-u-p-md">
          <template #icon><domain-icon /></template>

          <pf-title h="3">{{ $t('Zona DNS') }}</pf-title>
          <dns-records :dominio="services.dominio" />
        </pf-tab>

        <pf-tab v-if="services.id_registrazione_dominio" :title="$t('Dominio')" ouia-id="service-tab-domain" class="pf-v6-u-p-md">
          <template #icon><globe-icon /></template>

          <pf-title h="3">
            {{ $t('Trasferisci altrove il dominio') }}
            <pf-button
              v-if="!authcodeRequested"
              variant="control"
              inline
              @click="idAuthcodeRequest = services.id_registrazione_dominio"
              :ouia-id="`authcode-${services.dominio}`"
            >
              {{ $t('Richiedi AuthCode') }}
            </pf-button>
          </pf-title>
          <template v-if="authcodeRequested">
            <circle-check-icon color="green" /> {{ $t('Richiesta inviata, i nostri tecnici ti invieranno il codice authcode al più presto') }}
          </template>
        </pf-tab>
      </pf-tabs>

      <pf-alert v-else variant="danger" :title="$t('Errore: Servizi non trovati')" />
    </pf-page-section>
  </page>

  <prodotti-popup-renewal-change
    v-if="idAuthcodeRequest !== null"
    :idProduct="idAuthcodeRequest"
    variant="authcode"
    :renew="false"
    @close="idAuthcodeRequest = null"
    @submit="closeAuthcodeRequest()"
  />
</template>

<script lang="ts" setup>
import { $t as _t } from '@/i18n';
import { computed, ref, type Ref } from 'vue';
import { pop } from '@common/utils';
import { useRoute } from 'vue-router';
import FormInput from '@common/FormInput.vue';
import DnsRecords from '../components/dns-records.vue';
import ServiceDetailPannello from '../components/service-detail-pannello.vue';
import ProdottiPopupRenewalChange from '../components/prodotti-popup-renewal-change.vue';
import { useLoginStore } from '@/store/login';
import { ServiceResource, Resource } from '@/resources';
import { asyncComputed } from '@common/asyncComputed';
import ArrowUpRightFromSquareIcon from '@vue-patternfly/icons/arrow-up-right-from-square-icon';
import CircleNodesIcon from '@vue-patternfly/icons/circle-nodes-icon';
import DomainIcon from '@vue-patternfly/icons/domain-icon';
import ArrowIcon from '@vue-patternfly/icons/arrow-icon';
import EnvelopeIcon from '@vue-patternfly/icons/envelope-icon';
import MagnifyingGlassIcon from '@vue-patternfly/icons/magnifying-glass-icon';
import CircleCheckIcon from '@vue-patternfly/icons/circle-check-icon';
import GlobeIcon from '@vue-patternfly/icons/globe-icon';

const route = useRoute();
const domain = pop(route.params.domain);

const title = computed(() => (domain ? _t('Servizi associati al dominio {0}', [domain]) : 'asd'));

const { result: services, state: servicesState } = asyncComputed(async () => {
  if (domain) {
    return await new ServiceResource().get(domain);
  }
  return null;
});

const login = useLoginStore();

const titlePannello = computed(() => (services.value?.pannello && services.value?.posta ? 'Web' : _t('Web / Email')));

async function save(value: string, name: string | undefined) {
  if (!name || !services.value?.redirect) {
    return;
  }

  try {
    await new Resource('services').save(services.value.redirect.id, {
      [name]: value,
    });
  } catch (ex: any) {
    if (!ex.response || ![401, 404, 422].includes(ex.response.status)) {
      throw ex;
    }
  }
}

const idAuthcodeRequest: Ref<number | null> = ref(null);
const authcodeRequested = ref(false);

function closeAuthcodeRequest() {
  idAuthcodeRequest.value = null;
  authcodeRequested.value = true;
}

// URL fuori dal template: vue-tsc 3.3.13 interpreta il // delle URL nelle prop come commento (vuejs/language-tools#6240)
const pecWebmailUrl = 'https://mail.arterapec.it';
</script>
